import TabPageHeading from '../components/Layout/TabPageHeading';
import React, { useState, useEffect, useRef } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { Helmet } from 'react-helmet-async';
import { NewsPostSummary, NewsCategory, NEWS_CATEGORY_COLORS, NEWS_CATEGORY_LABELS } from '@radevents/shared';
import { buildNewsListSchema } from '../hooks/useNewsSchema';
import PublicHeader from '../components/Layout/PublicHeader';
import newsService from '../services/newsService';
import { trackNewsClick } from '../services/trackingService';

import { SITE_ORIGIN } from '../config/siteOrigin';
import { formatDayLong } from '../utils/format';

const FONT_DISPLAY = "'Archivo',sans-serif";
const FONT_BODY = "'Hanken Grotesk',sans-serif";
const FONT_MONO = "'Space Mono',monospace";

const PAGE_TITLE = 'Radsport News – Berichte, Vorschauen & Ergebnisse | Radsport Events';
const PAGE_DESC = 'Aktuelle Radsport-News: Berichte, Vorschauen und Ergebnisse rund um Rennrad-, Gravel- und MTB-Events in Deutschland, Österreich und der Schweiz.';
const CANONICAL = `${SITE_ORIGIN}/news`;

// Filter chips: die drei Disziplinen (wie bei den Events, ohne eigenen „Alle"-Button —
// keine Auswahl = alle Artikel; ein erneuter Klick auf die aktive Kategorie hebt den Filter auf).
const FILTER_CATS: Array<{ key: NewsCategory; label: string }> = [
  { key: NewsCategory.ROAD, label: 'Rennrad' },
  { key: NewsCategory.GRAVEL, label: 'Gravel' },
  { key: NewsCategory.MTB, label: 'MTB' },
];

export function categoryColor(cat: string): string {
  return NEWS_CATEGORY_COLORS[cat] ?? '#922C57';
}

export function categoryLabel(cat: string): string {
  return NEWS_CATEGORY_LABELS[cat] ?? cat;
}

/** Small rotated square used as the recurring diamond marker. */
export const Diamond: React.FC<{ size: number; color: string }> = ({ size, color }) => (
  <span style={{ width: size, height: size, borderRadius: 2, transform: 'rotate(45deg)', background: color, flexShrink: 0, display: 'inline-block' }} />
);

const PAGE_SIZE = 30;

/**
 * Einstieg fuer Veranstalter, die selbst einen Beitrag veroeffentlichen wollen. Steht als
 * Rasterkachel ueber die volle Breite und wiederholt sich alle sechs Artikel — wer hier liest,
 * sieht gerade, wo der eigene Beitrag stehen koennte.
 */
const PressCta: React.FC = () => (
  <aside style={{
    gridColumn: '1 / -1',
    padding: '22px 26px', borderRadius: 16, background: '#fff', border: '1px solid #E5E1E1',
    display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 20, flexWrap: 'wrap',
  }}>
    <div style={{ minWidth: 240, flex: 1 }}>
      <span style={{ fontFamily: FONT_MONO, fontSize: 11, letterSpacing: '0.16em', color: '#922C57' }}>
        {'// PRESSEMITTEILUNG EINREICHEN'}
      </span>
      <h2 style={{ fontFamily: FONT_DISPLAY, fontWeight: 800, fontSize: 20, lineHeight: 1.2, color: '#2C2C2C', margin: '8px 0 0' }}>
        Deine Veranstaltung hier vorstellen
      </h2>
      <p style={{ fontFamily: FONT_BODY, fontSize: 14, lineHeight: 1.6, color: '#6A6A6A', margin: '8px 0 0', maxWidth: 560 }}>
        Schick uns deinen Pressetext samt Fotos — wir veröffentlichen ihn als Newsbeitrag.
        Was wir dazu brauchen, steht auf der Kontaktseite.
      </p>
    </div>
    <Link
      to="/kontakt"
      style={{
        fontFamily: FONT_BODY, fontWeight: 700, fontSize: 14, whiteSpace: 'nowrap',
        background: '#922C57', color: '#fff', textDecoration: 'none',
        padding: '11px 22px', borderRadius: 999,
      }}
    >
      Pressetext einreichen ›
    </Link>
  </aside>
);

export default function News() {
  const navigate = useNavigate();
  const [posts, setPosts] = useState<NewsPostSummary[]>([]);
  const [loading, setLoading] = useState(true);
  const [loadingMore, setLoadingMore] = useState(false);
  const [currentPage, setCurrentPage] = useState(0);
  const [totalPages, setTotalPages] = useState(0);
  const [cat, setCat] = useState<'ALLE' | NewsCategory>('ALLE');
  const sentinelRef = useRef<HTMLDivElement>(null);

  const loadPosts = (page: number, append: boolean, category: 'ALLE' | NewsCategory) => {
    if (append) setLoadingMore(true); else setLoading(true);
    newsService.getAll(page, PAGE_SIZE, category === 'ALLE' ? undefined : category)
      .then(res => {
        setPosts(prev => (append ? [...prev, ...res.content] : res.content));
        setCurrentPage(res.page);
        setTotalPages(res.totalPages);
      })
      .catch(() => {})
      .finally(() => { setLoading(false); setLoadingMore(false); });
  };

  useEffect(() => {
    loadPosts(0, false, cat);
  }, [cat]);

  // Infinite scroll: nächste Seite laden, sobald der Sentinel sichtbar wird (wie auf der Event-Liste)
  useEffect(() => {
    const sentinel = sentinelRef.current;
    if (!sentinel) return;
    const observer = new IntersectionObserver(
      (entries) => {
        if (entries[0].isIntersecting && !loading && !loadingMore && currentPage + 1 < totalPages) {
          loadPosts(currentPage + 1, true, cat);
        }
      },
      { rootMargin: '200px' }
    );
    observer.observe(sentinel);
    return () => observer.disconnect();
  }, [loading, loadingMore, currentPage, totalPages, cat]);

  const filtered = posts;
  const lead = filtered[0];
  const rest = filtered.slice(1);

  return (
    <div className="has-bottom-nav" style={{ minHeight: '100vh', background: '#F5F3F3', display: 'flex', flexDirection: 'column' }}>
      <Helmet>
        <title>{PAGE_TITLE}</title>
        <meta name="description" content={PAGE_DESC} />
        <link rel="canonical" href={CANONICAL} />
        <meta property="og:title" content={PAGE_TITLE} />
        <meta property="og:description" content={PAGE_DESC} />
        <meta property="og:url" content={CANONICAL} />
        <meta property="og:type" content="website" />
        <meta name="twitter:card" content="summary" />
        <meta name="twitter:title" content={PAGE_TITLE} />
        <meta name="twitter:description" content={PAGE_DESC} />
        {posts.length > 0 && (
          <script type="application/ld+json">{JSON.stringify(buildNewsListSchema(posts))}</script>
        )}
      </Helmet>
      <style>{`
        @media (max-width: 900px) {
          .news-grid { grid-template-columns: repeat(2, 1fr) !important; }
          .news-lead { grid-template-columns: 1fr !important; }
          .news-lead-img { min-height: 220px !important; }
        }
        @media (max-width: 640px) {
          .news-grid { grid-template-columns: 1fr !important; }
          /* Drei Kategorien gleichmäßig in einer Zeile */
          .news-filter { margin: 0 0 16px !important; gap: 7px !important; }
          .news-filter > button { flex: 1 1 0 !important; padding: 10px 8px !important; }
        }
      `}</style>

      <PublicHeader />

      <div className="news-container tab-page-content" style={{ flex: 1, width: '100%', boxSizing: 'border-box', maxWidth: 1180, margin: '0 auto', padding: '28px 40px 60px' }}>
        <TabPageHeading unsichtbar>News</TabPageHeading>

        {/* Kategorie-Filter — drei Disziplinen in einer Zeile, ohne „Alle" (Klick auf aktive = zurücksetzen) */}
        <div className="news-filter" style={{ display: 'flex', gap: 8, margin: '0 0 20px' }}>
          {FILTER_CATS.map(c => {
            const active = c.key === cat;
            const color = categoryColor(c.key);
            return (
              <button
                key={c.key}
                onClick={() => setCat(active ? 'ALLE' : c.key)}
                style={{
                  display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 8,
                  cursor: 'pointer', padding: '11px 16px', borderRadius: 10, minWidth: 0,
                  fontFamily: FONT_BODY, fontWeight: 600, fontSize: 14,
                  background: active ? `${color}1A` : '#fff',
                  color: active ? color : '#2C2C2C',
                  border: `1px solid ${active ? color : '#E5E1E1'}`,
                }}
              >
                <Diamond size={9} color={color} />
                {c.label}
              </button>
            );
          })}
        </div>

        {loading ? (
          <div style={{ fontFamily: FONT_BODY, color: '#6A6A6A' }}>Lade Artikel…</div>
        ) : filtered.length === 0 ? (
          <div style={{ padding: 50, textAlign: 'center', background: '#fff', border: '1px solid #E5E1E1', borderRadius: 14, color: '#6A6A6A', fontFamily: FONT_BODY }}>
            Keine Artikel in dieser Kategorie.
          </div>
        ) : (
          <>
            {/* Aufmacher */}
            <article
              className="news-lead"
              onClick={() => { trackNewsClick(lead.slug, 'news-lead'); navigate(`/news/${lead.slug}`); }}
              style={{ display: 'grid', gridTemplateColumns: '1.15fr 1fr', gap: 0, background: 'linear-gradient(135deg,#3A2E34 0%,#922C57 135%)', borderRadius: 18, overflow: 'hidden', cursor: 'pointer', marginBottom: 26 }}
            >
              <div
                className="news-lead-img"
                style={{ position: 'relative', minHeight: 320, backgroundImage: lead.coverImageUrl ? `url('${lead.coverImageUrl}')` : undefined, backgroundSize: 'cover', backgroundPosition: 'center', backgroundColor: '#3A2E34' }}
              />
              <div style={{ padding: '34px 34px 30px', display: 'flex', flexDirection: 'column', justifyContent: 'center' }}>
                <span style={{ display: 'inline-flex', alignItems: 'center', gap: 8, fontFamily: FONT_BODY, fontVariantNumeric: 'tabular-nums', fontWeight: 600, fontSize: 12, letterSpacing: '0.05em', textTransform: 'uppercase', color: 'rgba(245,243,243,0.75)' }}>
                  <Diamond size={8} color={categoryColor(lead.category)} />
                  {categoryLabel(lead.category)} · {formatDayLong(lead.publishedAt)}
                </span>
                <h2 style={{ fontFamily: FONT_DISPLAY, fontWeight: 600, fontSize: 30, lineHeight: 1.08, letterSpacing: '-0.01em', color: '#fff', margin: '14px 0 0' }}>{lead.title}</h2>
                <p style={{ fontFamily: FONT_BODY, fontSize: 15, lineHeight: 1.6, color: 'rgba(245,243,243,0.82)', margin: '14px 0 0' }}>{lead.teaser}</p>
                <span style={{ display: 'inline-flex', alignItems: 'center', gap: 7, marginTop: 20, fontFamily: FONT_BODY, fontWeight: 700, fontSize: 14, color: '#fff' }}>Artikel lesen →</span>
              </div>
            </article>

            {/* Grid */}
            <div className="news-grid" style={{ display: 'grid', gridTemplateColumns: 'repeat(3, 1fr)', gap: 20 }}>
              {rest.map((n, i) => (
                <React.Fragment key={n.id}>
                <article
                  onClick={() => { trackNewsClick(n.slug, 'news-grid'); navigate(`/news/${n.slug}`); }}
                  onMouseEnter={e => { e.currentTarget.style.borderColor = '#2C2C2C'; e.currentTarget.style.boxShadow = '0 14px 34px rgba(0,0,0,0.09)'; }}
                  onMouseLeave={e => { e.currentTarget.style.borderColor = '#E5E1E1'; e.currentTarget.style.boxShadow = 'none'; }}
                  style={{ display: 'flex', flexDirection: 'column', background: '#fff', border: '1px solid #E5E1E1', borderRadius: 16, overflow: 'hidden', cursor: 'pointer', transition: 'border-color .15s, box-shadow .15s' }}
                >
                  <div style={{ position: 'relative', height: 172, backgroundImage: n.coverImageUrl ? `url('${n.coverImageUrl}')` : undefined, backgroundSize: 'cover', backgroundPosition: 'center', backgroundColor: '#EEEBF5' }}>
                    <span style={{ position: 'absolute', left: 12, top: 12, display: 'inline-flex', alignItems: 'center', gap: 7, fontFamily: FONT_MONO, fontSize: 10, letterSpacing: '0.05em', textTransform: 'uppercase', color: '#fff', background: 'rgba(38,38,38,0.72)', backdropFilter: 'blur(4px)', padding: '5px 10px', borderRadius: 999 }}>
                      <Diamond size={7} color={categoryColor(n.category)} />
                      {categoryLabel(n.category)}
                    </span>
                  </div>
                  <div style={{ padding: '18px 18px 20px', display: 'flex', flexDirection: 'column', flex: 1 }}>
                    <span style={{ fontFamily: FONT_BODY, fontVariantNumeric: 'tabular-nums', fontWeight: 500, fontSize: 12.5, letterSpacing: '0.05em', color: '#9A9494' }}>{formatDayLong(n.publishedAt)}</span>
                    <h3 style={{ fontFamily: FONT_DISPLAY, fontWeight: 600, fontSize: 18, lineHeight: 1.15, color: '#2C2C2C', margin: '9px 0 0' }}>{n.title}</h3>
                    <p style={{ fontFamily: FONT_BODY, fontSize: 13.5, lineHeight: 1.55, color: '#6A6A6A', margin: '9px 0 0' }}>{n.teaser}</p>
                    <span style={{ marginTop: 'auto', paddingTop: 14, fontFamily: FONT_BODY, fontWeight: 700, fontSize: 13, color: '#922C57' }}>Weiterlesen ›</span>
                  </div>
                </article>
                {/* Nach jeweils sechs Artikeln — bei hunderten Beiträgen wäre ein einzelner
                    Einstieg am Listenende praktisch nie erreichbar. */}
                {(i + 1) % 6 === 0 && <PressCta />}
                </React.Fragment>
              ))}
              {/* Kurze Listen erreichen die Sechser-Marke nie und bekämen sonst gar keinen. */}
              {rest.length < 6 && <PressCta />}
            </div>

            {/* Infinite-Scroll-Sentinel + Ladeindikator */}
            <div ref={sentinelRef} style={{ height: 1 }} />
            {loadingMore && (
              <div style={{ padding: 24, textAlign: 'center', fontFamily: FONT_BODY, color: '#6A6A6A' }}>
                Lade weitere Artikel…
              </div>
            )}
          </>
        )}

      </div>

    </div>
  );
}
